import {
  Box,
  Column,
  Row,
  Text,
  WarningIcon,
  TextWithTooltip,
} from "@hightouch/htui";
import type { DecisionEngineChannelType } from "@hightouch/lib/customer-data/decision-engine/types";

import { Card } from "src/components/card";
import { Link } from "src/router";
import { Table } from "src/ui/table";
import { IntegrationIcon } from "src/components/integrations/integration-icon";
import { ChannelIcon } from "src/pages/decision-engines/components/channel-icon";
import { ChannelOptionsMap } from "src/pages/decision-engines/utils";
import { EventIcon } from "src/pages/schema/icons";
import type { useDecisionEngineContext } from "src/pages/decision-engines/decision-engine-context";

type EngineChannel = ReturnType<
  typeof useDecisionEngineContext
>["engine"]["channels"][number];

type ChannelSendEvent = {
  segment?: { id: string; name: string } | null;
  attribution?: { campaign_id_column?: string | null } | null;
} | null;

function getSendEvent(channel: EngineChannel): ChannelSendEvent {
  return (channel as { send_event?: ChannelSendEvent }).send_event ?? null;
}

type Props = {
  channels: EngineChannel[];
};

export function ChannelConfigurationCard({ channels }: Props) {
  const enabledChannels = channels.filter((ch) => ch.enabled);

  if (enabledChannels.length === 0) return null;

  return (
    <Card gap={4} overflow="hidden">
      <Column gap={1}>
        <Text size="lg" fontWeight="medium" color="text.primary">
          Channel configuration
        </Text>
        <Text size="md" fontWeight="normal" color="text.secondary">
          Send event and campaign attribution for each enabled channel.
        </Text>
      </Column>

      <Box sx={{ "& tr:last-child > td": { border: "none" } }}>
        <Table
          primaryKey="id"
          data={enabledChannels}
          columns={[
            {
              name: "Channel",
              cell: ({ id, type, label }) => (
                <Row align="center" width="100%" gap={3} overflow="hidden">
                  <ChannelIcon
                    size="md"
                    type={type as DecisionEngineChannelType}
                  />
                  <Link
                    href={`/ai/configuration/channels/${id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {label || ChannelOptionsMap[type]?.label || type}
                  </Link>
                </Row>
              ),
            },
            {
              name: "Destination",
              min: "20%",
              cell: ({ destination }) => (
                <Row align="center" gap={3} overflow="hidden">
                  <IntegrationIcon
                    name={destination?.definition.name}
                    src={destination?.definition.icon}
                  />
                  <TextWithTooltip fontWeight="medium">
                    {destination?.name ?? destination?.definition.name}
                  </TextWithTooltip>
                </Row>
              ),
            },
            {
              name: "Send Event",
              cell: (channel) => {
                const sendEvent = getSendEvent(channel);
                if (sendEvent?.segment) {
                  return (
                    <Row align="center" gap={3} overflow="hidden">
                      <EventIcon size="md" />
                      <Link
                        href={`/schema/events/${sendEvent.segment.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {sendEvent.segment.name}
                      </Link>
                    </Row>
                  );
                }
                return (
                  <Row align="center" gap={2} overflow="hidden">
                    <Box as={WarningIcon} color="warning.base" flexShrink={0} />
                    <Text color="warning.base" fontWeight="medium">
                      No send event configured
                    </Text>
                  </Row>
                );
              },
            },
            {
              name: "Campaign ID",
              cell: (channel) => {
                const sendEvent = getSendEvent(channel);
                const campaignIdColumn =
                  sendEvent?.attribution?.campaign_id_column;
                if (campaignIdColumn) {
                  return (
                    <Row align="center" gap={2} overflow="hidden">
                      <Text
                        fontWeight="medium"
                        variant="monospace"
                        size="sm"
                        color="text.primary"
                      >
                        {campaignIdColumn}
                      </Text>
                    </Row>
                  );
                }
                return (
                  <Row align="center" gap={2} overflow="hidden">
                    <Box as={WarningIcon} color="warning.base" flexShrink={0} />
                    <Text color="warning.base" fontWeight="medium">
                      Not configured
                    </Text>
                  </Row>
                );
              },
            },
          ]}
        />
      </Box>
    </Card>
  );
}
